import {
  AlertTriangle,
  Clock,
  DollarSign,
  Globe,
  Mail,
  MapPin,
  PhoneCall,
  PhoneForwarded,
  Plus,
  Route,
  Trash2,
} from 'lucide-react';
import { Button, Input } from '@/shared/ui';
import { validatePhone, formatPhoneDisplay, PHONE_PLACEHOLDER } from '@/shared/utils/validation';
import { useState } from 'react';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from '../pages/AgentsPage.module.css';
import { TRANSFERABLE_INTENT_OPTIONS } from '../agentShared';
import { parseTopicsMarkdown } from '../topicsMarkdown';
import {
  EmailPillInput,
  ChipListInput,
  LanguageToggle,
  SectionCard,
  ToggleRow,
} from './builderShared';
import type { SectionProps } from './builderShared';

interface OpenProps {
  open: boolean;
  onToggle: () => void;
}

// ── Check-list row (self-serve / transferable / collect items) ────────────────

function CheckItem({
  checked,
  locked,
  label,
  hint,
  onToggle,
}: {
  checked: boolean;
  locked?: boolean;
  label: string;
  hint?: string;
  onToggle?: () => void;
}) {
  return (
    <div className={styles.collectItem}>
      <button
        type="button"
        className={[
          styles.collectCheck,
          checked ? styles.collectCheckOn : '',
          locked ? styles.collectCheckLocked : '',
        ]
          .filter(Boolean)
          .join(' ')}
        disabled={locked || !onToggle}
        onClick={onToggle}
        aria-label={label}
        aria-pressed={checked}
      >
        {checked ? '✓' : ''}
      </button>
      <span className={styles.collectLabel}>
        {label}
        {hint && <span className={styles.hintText}> {hint}</span>}
      </span>
      {locked && <span className={styles.lockTag}>ALWAYS ON</span>}
    </div>
  );
}

// ── Script & persona ──────────────────────────────────────────────────────────

export function ScriptSection({
  draft,
  template,
  set,
  setConfig,
  open,
  onToggle,
}: SectionProps & OpenProps) {
  const done = !!(draft.name.trim() && draft.greeting.trim());
  const isSpanish = template.language === 'es';
  const showCallbackPromise = template.defaults.config.callbackPromise !== undefined;
  const isReschedule = template.id === 'reschedule';

  return (
    <SectionCard
      icon={PhoneCall}
      title={isReschedule ? 'Script & the save attempt' : 'Script & persona'}
      done={done}
      open={open}
      onToggle={onToggle}
    >
      <label className={styles.fieldLabel}>Use-case name</label>
      <Input
        value={draft.name}
        onChange={(e) => set('name', e.target.value)}
        placeholder="e.g. After-hours emergency line"
        fullWidth
      />
      {template.id !== 'emergency' && (
        <>
          <label className={styles.fieldLabel}>
            Opening line (what the agent says first{isSpanish ? ', in Spanish' : ''})
          </label>
          <textarea
            className={patterns.textarea}
            rows={3}
            value={draft.greeting}
            onChange={(e) => set('greeting', e.target.value)}
            placeholder="Thanks for calling…"
          />
        </>
      )}
      {isSpanish && template.defaults.greetingGloss && (
        <div className={styles.glossBox}>
          <span className={styles.glossLabel}>English (reference)</span>
          {template.defaults.greetingGloss}
        </div>
      )}
      {template.id === 'assist' && (
        <p className={styles.hintText}>
          The opener adapts automatically when the agent picks up on overflow ("our team is helping
          other patients right now") or during lunch cover.
        </p>
      )}
      {showCallbackPromise && (
        <>
          <label className={styles.fieldLabel}>Callback promise</label>
          <Input
            value={draft.config.callbackPromise ?? ''}
            onChange={(e) => setConfig('callbackPromise', e.target.value)}
            placeholder="within the hour during business hours"
            fullWidth
          />
        </>
      )}
      {isReschedule && (
        <>
          <ToggleRow
            title="Try to save cancellations"
            description="When someone wants to cancel, the agent makes one gentle offer to move the visit instead. A second “no” is always accepted immediately — never pushy."
            on={draft.config.saveAttemptEnabled !== false}
            onToggle={() =>
              setConfig('saveAttemptEnabled', !(draft.config.saveAttemptEnabled !== false))
            }
          />
          {draft.config.saveAttemptEnabled !== false && (
            <>
              <label className={styles.fieldLabel}>The save line (said once)</label>
              <textarea
                className={patterns.textarea}
                rows={2}
                value={draft.config.saveLine ?? ''}
                onChange={(e) => setConfig('saveLine', e.target.value)}
              />
            </>
          )}
        </>
      )}
      {template.id === 'referral' && (
        <>
          <label className={styles.fieldLabel}>Procedures the agent can accept referrals for</label>
          <ChipListInput
            value={draft.config.procedures ?? []}
            onChange={(v) => setConfig('procedures', v)}
            placeholder="Add…"
          />
          <p className={styles.hintText}>
            Prefilled for endodontics — edit to match your specialty.
          </p>
        </>
      )}
      <label className={styles.fieldLabel}>
        <Globe size={11} className={styles.labelIcon} />
        Response languages
      </label>
      <LanguageToggle languages={draft.languages} onChange={(langs) => set('languages', langs)} />
    </SectionCard>
  );
}

// ── Routing & hours (when the agent picks up) ─────────────────────────────────

const ROUTING_MODES = [
  {
    key: 'assist_first' as const,
    title: 'Assist first',
    recommended: true,
    desc: 'The agent answers every call, handles the quick ones itself, and passes the rest to your team. Your desk phones only ring for calls that need a person.',
  },
  {
    key: 'overflow' as const,
    title: 'Overflow',
    recommended: false,
    desc: 'Your team gets first crack. The agent answers when the line is busy or unanswered.',
  },
  {
    key: 'scheduled' as const,
    title: 'Lunch & breaks',
    recommended: false,
    desc: 'Agent covers the times your phone system forwards to it — e.g. 12–1 pm daily — and knows to say the team is at lunch. Set that schedule in your phone system’s forwarding rules.',
  },
];

const RING_OPTIONS = [15, 20, 30];

export function RoutingSection({ draft, setConfig, open, onToggle }: SectionProps & OpenProps) {
  const mode = draft.config.routingMode ?? 'assist_first';
  return (
    <SectionCard
      icon={Route}
      title="Routing & hours — when the agent picks up"
      done={!!draft.hours.trim()}
      open={open}
      onToggle={onToggle}
    >
      <div className={styles.modeGrid}>
        {ROUTING_MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            className={`${styles.modeCard} ${mode === m.key ? styles.modeCardSelected : ''}`}
            onClick={() => setConfig('routingMode', m.key)}
          >
            <span className={styles.modeTitle}>
              <span
                className={`${styles.modeRadio} ${mode === m.key ? styles.modeRadioSelected : ''}`}
              />
              {m.title}
              {m.recommended && <span className={styles.recBadge}>RECOMMENDED</span>}
            </span>
            <span className={styles.modeDesc}>{m.desc}</span>
          </button>
        ))}
      </div>
      <div className={styles.fieldRow}>
        <div>
          <label className={styles.fieldLabel}>
            <Clock size={11} className={styles.labelIcon} />
            Business hours
          </label>
          <Input value={draft.hours} disabled fullWidth />
          <p className={styles.hintText}>Pulled from your Location settings.</p>
        </div>
        <div>
          <label className={styles.fieldLabel}>Ring your desk for (overflow mode)</label>
          <select
            className={styles.activateSelect}
            value={String(draft.config.ringSeconds ?? 20)}
            onChange={(e) => setConfig('ringSeconds', Number(e.target.value))}
          >
            {RING_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s} seconds{s === 20 ? ' (about 4 rings)' : ''}
              </option>
            ))}
          </select>
          <p className={styles.hintText}>
            Set the same ring time in your phone system's forwarding — that's what actually decides
            when the agent picks up.
          </p>
        </div>
      </div>
      <div className={styles.infoRow}>
        <Globe size={12} /> Forwarding is set on your phone system, not here. Pick your provider in
        the panel on the right for steps and how to test it with a call once your number is live.
      </div>
    </SectionCard>
  );
}

// ── Handles vs. hands off (Front-Desk Assist) ─────────────────────────────────

export function HandoffSection({
  draft,
  set,
  setConfig,
  open,
  onToggle,
}: SectionProps & OpenProps) {
  const intents = draft.config.transferableIntents ?? [];
  const captureOnly = draft.config.captureOnlyMode !== false;
  const [transferNumberError, setTransferNumberError] = useState<string | null>(null);

  const toggleIntent = (key: string) =>
    setConfig(
      'transferableIntents',
      intents.includes(key) ? intents.filter((i) => i !== key) : [...intents, key],
    );

  return (
    <SectionCard
      icon={PhoneForwarded}
      title="What the agent handles vs. hands to your team"
      done
      open={open}
      onToggle={onToggle}
    >
      <label className={styles.fieldLabel}>
        The agent handles these itself — your desk never rings
      </label>
      <div>
        <CheckItem checked locked label="Hours, address & directions" />
        <CheckItem checked locked label="Running-late messages (instant note to your desk)" />
        <CheckItem checked locked label="Callback requests, captured & prioritized" />
        <CheckItem checked label="FAQ answers — from your knowledge below" />
        <CheckItem
          checked
          label="Confirming which insurance you accept"
          hint="(from your list — never guesses coverage)"
        />
      </div>

      <label className={styles.fieldLabel}>
        Hand these to the front desk — when your team is available
      </label>
      <div>
        {TRANSFERABLE_INTENT_OPTIONS.map((o) => (
          <CheckItem
            key={o.key}
            checked={intents.includes(o.key)}
            label={o.label}
            hint={
              o.key === 'booking_request'
                ? '(a person has to book — no practice-system integration yet)'
                : undefined
            }
            onToggle={() => toggleIntent(o.key)}
          />
        ))}
      </div>

      <div className={styles.fieldRow}>
        <div>
          <label className={styles.fieldLabel}>
            <PhoneForwarded size={11} className={styles.labelIcon} />
            Front-desk line to transfer to
          </label>
          <Input
            value={draft.transferNumber}
            onChange={(e) => {
              set('transferNumber', e.target.value);
              if (transferNumberError) setTransferNumberError(null);
            }}
            onBlur={(e) => {
              const formatted = formatPhoneDisplay(e.target.value);
              set('transferNumber', formatted);
              setTransferNumberError(validatePhone(formatted));
            }}
            placeholder={PHONE_PLACEHOLDER}
            error={transferNumberError ?? undefined}
            fullWidth
          />
        </div>
        <div>
          <label className={styles.fieldLabel}>Ring the desk for</label>
          <select
            className={styles.activateSelect}
            value={String(draft.config.transferRingSeconds ?? 20)}
            onChange={(e) => setConfig('transferRingSeconds', Number(e.target.value))}
          >
            {RING_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s} seconds
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.successRow}>
        If nobody picks up, the agent takes over seamlessly: it captures everything with the
        caller's original request attached ("wanted to book"), and adds a prioritized callback for
        your team. Outside business hours it skips the transfer and goes straight to capture.
      </div>

      <ToggleRow
        title="Capture-only mode"
        description="Turn off all transfers — the agent takes a complete message for every call instead. Recommended until live-transfer audio is fully verified for your line."
        on={captureOnly}
        onToggle={() => setConfig('captureOnlyMode', !captureOnly)}
      />
    </SectionCard>
  );
}

// ── Knowledge & FAQ ───────────────────────────────────────────────────────────

const EMERGENCY_KNOWLEDGE_SUBTITLE =
  "Organize answers as topics — each one a single focused subject (Office Hours, Insurance, Broken Tooth…). Under Possible Questions, list every way a caller might actually phrase it; the agent matches what's said to the closest topic. Response is the one script it reads back for that topic, so write it as a complete, ready-to-speak answer rather than a fragment. You can write this directly or paste content generated elsewhere (e.g. by an AI tool) — it's parsed automatically.";

export function KnowledgeSection({
  draft,
  template,
  set,
  setConfig,
  open,
  onToggle,
}: SectionProps & OpenProps) {
  const done = !!(draft.hours.trim() && draft.address.trim());
  const isSpanish = template.language === 'es';
  const isEmergency = template.id === 'emergency';

  const setFaq = (i: number, field: 'q' | 'a', value: string) =>
    set(
      'faqs',
      draft.faqs.map((f, idx) => (idx === i ? { ...f, [field]: value } : f)),
    );

  const [showExample, setShowExample] = useState(false);

  const { topics: parsedTopics, warnings: topicWarnings } = isEmergency
    ? parseTopicsMarkdown(draft.topicsMarkdown)
    : { topics: [], warnings: [] };

  return (
    <SectionCard
      icon={Globe}
      title="Knowledge & FAQ"
      sub={
        isEmergency ? (
          <>
            {EMERGENCY_KNOWLEDGE_SUBTITLE}
            <button
              type="button"
              className={styles.linkButton}
              onClick={(e) => {
                e.stopPropagation();
                setShowExample((v) => !v);
              }}
            >
              {showExample ? 'Hide example' : 'Show example'}
            </button>
          </>
        ) : isSpanish ? (
          'Spanish, reviewed in English'
        ) : undefined
      }
      done={done}
      open={open}
      onToggle={onToggle}
    >
      {!isEmergency && (
        <>
          <div className={styles.infoRow}>
            <Globe size={12} /> Hours and address come from your Location settings and can't be
            edited here — the agent already answers "what are your hours?" and "where are you
            located?" using these.
          </div>
          <div className={styles.fieldRow}>
            <div>
              <label className={styles.fieldLabel}>
                <Clock size={11} className={styles.labelIcon} />
                Hours
              </label>
              <Input value={draft.hours} disabled fullWidth />
            </div>
            <div>
              <label className={styles.fieldLabel}>
                <MapPin size={11} className={styles.labelIcon} />
                Address
              </label>
              <Input value={draft.address} disabled fullWidth />
            </div>
          </div>
        </>
      )}

      {template.id === 'newpatient' && (
        <>
          <label className={styles.fieldLabel}>New-patient forms link</label>
          <Input
            value={draft.config.formsLink ?? ''}
            onChange={(e) => setConfig('formsLink', e.target.value)}
            placeholder="https://yourpractice.com/new-patient-forms"
            fullWidth
          />
          <p className={styles.hintText}>
            The agent reads this out (or spells it) when callers ask how to fill out forms ahead of
            their visit.
          </p>
        </>
      )}

      {template.id === 'assist' && (
        <>
          <label className={styles.fieldLabel}>Insurance the agent can confirm you accept</label>
          <ChipListInput
            value={draft.config.insuranceList ?? []}
            onChange={(v) => setConfig('insuranceList', v)}
            placeholder="Add carrier…"
          />
          <p className={styles.hintText}>
            For anything else the agent says "let me have the team confirm that on your callback" —
            it never guesses coverage.
          </p>
        </>
      )}

      {!isEmergency && <label className={styles.fieldLabel}>Answers the agent can give</label>}
      {isSpanish && (
        <p className={styles.hintText}>
          Write questions and answers in Spanish — the whole call stays in Spanish. Summary emails
          arrive in English; the full Spanish transcript is in the Call Log.
        </p>
      )}
      {isEmergency ? (
        <>
          {showExample && (
            <pre className={styles.formatHint}>
              {
                '## Topic name\n\nPossible Questions:\n- question one\n- question two\n\nResponse:\n\nThe agent’s full answer.\n\n---'
              }
            </pre>
          )}
          <textarea
            className={patterns.textarea}
            rows={24}
            value={draft.topicsMarkdown}
            onChange={(e) => set('topicsMarkdown', e.target.value)}
            placeholder={
              '## Office Hours\n\nPossible Questions:\n- What are your office hours?\n\nResponse:\n\nWe are open...'
            }
          />
          {topicWarnings.length > 0 ? (
            <div className={styles.hintTextWarning}>
              <AlertTriangle size={12} className={styles.labelIcon} />
              {topicWarnings.length} thing{topicWarnings.length === 1 ? '' : 's'} to fix before this
              is complete:
              <ul>
                {topicWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          ) : (
            parsedTopics.length > 0 && (
              <p className={styles.hintText}>
                ✓ {parsedTopics.length} topic{parsedTopics.length === 1 ? '' : 's'} parsed.
              </p>
            )
          )}
        </>
      ) : (
        <>
          {draft.faqs.map((f, i) => (
            <div key={i} className={styles.faqItem}>
              <div className={styles.faqItemRow}>
                <Input
                  value={f.q}
                  onChange={(e) => setFaq(i, 'q', e.target.value)}
                  placeholder="Question"
                  fullWidth
                />
                <button
                  onClick={() =>
                    set(
                      'faqs',
                      draft.faqs.filter((_, idx) => idx !== i),
                    )
                  }
                  className={styles.deleteBtn}
                  aria-label="Remove FAQ"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <textarea
                className={patterns.textarea}
                rows={2}
                value={f.a}
                onChange={(e) => setFaq(i, 'a', e.target.value)}
                placeholder="Answer"
              />
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={13} />}
            onClick={() => set('faqs', [...draft.faqs, { q: '', a: '' }])}
          >
            Add answer
          </Button>
        </>
      )}
    </SectionCard>
  );
}

// ── Cancellation policy (Reschedule Saver) ────────────────────────────────────

export function PolicySection({ draft, setConfig, open, onToggle }: SectionProps & OpenProps) {
  return (
    <SectionCard
      icon={AlertTriangle}
      title="Policy the agent may state"
      done={!!draft.config.cancellationPolicy?.trim()}
      open={open}
      onToggle={onToggle}
    >
      <label className={styles.fieldLabel}>Your cancellation policy, in the agent's words</label>
      <textarea
        className={patterns.textarea}
        rows={2}
        value={draft.config.cancellationPolicy ?? ''}
        onChange={(e) => setConfig('cancellationPolicy', e.target.value)}
      />
      <div className={styles.warningRow}>
        <AlertTriangle size={12} /> The agent states only what's written here. It never invents fees
        or exceptions — anything else is deferred to the team.
      </div>
    </SectionCard>
  );
}

// ── Callback language (Línea en Español) ──────────────────────────────────────

export function CallbackLanguageSection({
  draft,
  setConfig,
  open,
  onToggle,
}: SectionProps & OpenProps) {
  const staffed = draft.config.spanishCallbackStaff === true;
  return (
    <SectionCard
      icon={Mail}
      title="Callbacks in which language?"
      done
      open={open}
      onToggle={onToggle}
    >
      <ToggleRow
        title="Our team has Spanish-speaking staff for callbacks"
        description="ON: the agent promises a Spanish callback. OFF: the agent asks each caller whether an English callback is okay or they'd rather wait for a Spanish-speaking callback — so your desk never gets a callback it can't handle."
        on={staffed}
        onToggle={() => setConfig('spanishCallbackStaff', !staffed)}
      />
      {!staffed && (
        <div className={styles.warningRow}>
          <AlertTriangle size={12} /> With this off, each queue entry shows the caller's preference:
          Spanish callback · English okay.
        </div>
      )}
    </SectionCard>
  );
}

// ── Alerts & follow-up ────────────────────────────────────────────────────────

const SMS_RECAP_COPY: Record<string, { title: string; desc: string }> = {
  assist: {
    title: 'Text the caller a recap',
    desc: "After the call (with their okay): who you are, when you'll call back, and your main line.",
  },
  campaign: {
    title: 'Text the caller a confirmation',
    desc: 'Offer name, when to expect your call, and your booking link if you have one.',
  },
  newpatient: {
    title: 'Text new-patient forms after the call',
    desc: 'With the caller’s okay, they get a text with your forms link and practice details. Insurance and birth-date details are never included in texts.',
  },
  spanish: {
    title: 'Text the caller a confirmation (Spanish)',
    desc: 'Confirmation texts to callers are sent in Spanish.',
  },
  reschedule: {
    title: 'Text the caller a confirmation',
    desc: 'Confirms the change request in writing.',
  },
};

export function AlertsSection({
  draft,
  template,
  set,
  setConfig,
  open,
  onToggle,
}: SectionProps & OpenProps) {
  const routeDone = draft.notifyEmails.length > 0;
  const cfg = draft.config;
  const smsCopy = SMS_RECAP_COPY[template.id];
  const showTransfer = template.id === 'emergency' || template.id === 'custom';
  const [transferNumberError, setTransferNumberError] = useState<string | null>(null);

  return (
    <SectionCard
      icon={Mail}
      title="Alerts & follow-up"
      done={routeDone}
      open={open}
      onToggle={onToggle}
    >
      <ToggleRow
        title={
          template.language === 'es' ? 'Emergency safety rules (Spanish)' : 'Emergency safety rules'
        }
        description={
          template.language === 'es'
            ? 'Life-threatening symptoms → "cuelgue y llame al 9-1-1." Reviewed by a native speaker; can’t be turned off.'
            : 'Life-threatening symptoms are always directed to 911, and severe dental pain is flagged urgent. This can’t be turned off.'
        }
        locked
      />

      {/* Caller texting isn't connected to call agents yet — shown locked off
          so nobody assumes a text went out. */}
      {smsCopy && (
        <ToggleRow
          title={smsCopy.title}
          description={smsCopy.desc}
          lockedOff
          lockedLabel="NOT LIVE YET"
        />
      )}
      {template.id === 'assist' && (
        <ToggleRow
          title="Text directions on request"
          description="When a caller asks where you are, the agent offers to text your address with a maps link."
          lockedOff
          lockedLabel="NOT LIVE YET"
        />
      )}
      {template.id === 'referral' && (
        <>
          <ToggleRow
            title="Referral confirmation email"
            description="After the call, a structured referral confirmation is emailed to your front desk, including the referring office's email as heard on the call — verify it before forwarding, since the email contains patient details. The agent reads the full referral back before ending the call."
            on={cfg.confirmBothOffices !== false}
            onToggle={() => setConfig('confirmBothOffices', !(cfg.confirmBothOffices !== false))}
          />
          <ToggleRow
            title="Emergency referral alert"
            description={'"Emergency — today" referrals ping your front desk instantly.'}
            on={cfg.emergencyReferralAlertEnabled !== false}
            onToggle={() =>
              setConfig(
                'emergencyReferralAlertEnabled',
                !(cfg.emergencyReferralAlertEnabled !== false),
              )
            }
          />
          <ToggleRow
            title="Text messages"
            description="Disabled for this line — referral details include patient information and are sent by email only."
            lockedOff
          />
        </>
      )}
      {template.id === 'campaign' && (
        <ToggleRow
          title="Hot-lead alerts"
          description="If a promising caller doesn't commit, your front desk gets an email within 5 minutes with what they asked and why they hesitated — so you can call back while they're still deciding."
          on={cfg.hotLeadAlertEnabled !== false}
          onToggle={() => setConfig('hotLeadAlertEnabled', !(cfg.hotLeadAlertEnabled !== false))}
        />
      )}
      {template.id === 'reschedule' && (
        <ToggleRow
          title="Same-day cancellation alert"
          description="A cancellation within 24 hours pings your front desk immediately — “Slot to fill: Thu 2:30 PM” — so you can offer it to your waitlist."
          on={cfg.sameDayAlertEnabled !== false}
          onToggle={() => setConfig('sameDayAlertEnabled', !(cfg.sameDayAlertEnabled !== false))}
        />
      )}
      {template.id !== 'referral' && template.id !== 'emergency' && (
        <ToggleRow
          title="Instant alert on urgent calls"
          description="Email your front desk the moment a call is flagged urgent, instead of waiting for the summary email."
          on={cfg.urgentAlertEnabled !== false}
          onToggle={() => setConfig('urgentAlertEnabled', !(cfg.urgentAlertEnabled !== false))}
        />
      )}
      {(template.id === 'assist' ||
        template.id === 'campaign' ||
        template.id === 'newpatient' ||
        template.id === 'spanish' ||
        template.id === 'reschedule') && (
        <p className={styles.hintText}>
          Texts to callers aren't connected to call agents yet — no text is sent today. Email alerts
          work now.
        </p>
      )}

      <label className={styles.fieldLabel}>
        <Mail size={11} className={styles.labelIcon} />
        {template.id === 'newpatient'
          ? 'Intake summaries go to'
          : template.id === 'referral'
            ? 'Referral confirmations go to'
            : 'Call summaries go to'}
      </label>
      <EmailPillInput value={draft.notifyEmails} onChange={(v) => set('notifyEmails', v)} />
      <p className={styles.hintText}>
        {template.id === 'referral'
          ? 'The referring office’s email is captured on the call and shown in the confirmation for you to verify — patient details are never emailed to it automatically.'
          : 'After every call, the front desk gets an email with caller, callback, reason, and what the agent did.'}
      </p>

      {showTransfer && (
        <>
          <div className={styles.fieldRow}>
            <div>
              <label className={styles.fieldLabel}>
                <PhoneForwarded size={11} className={styles.labelIcon} />
                Warm-transfer to (during open hours)
              </label>
              <Input
                value={draft.transferNumber}
                onChange={(e) => {
                  set('transferNumber', e.target.value);
                  if (transferNumberError) setTransferNumberError(null);
                }}
                onBlur={(e) => {
                  const formatted = formatPhoneDisplay(e.target.value);
                  set('transferNumber', formatted);
                  setTransferNumberError(validatePhone(formatted));
                }}
                placeholder={PHONE_PLACEHOLDER}
                error={transferNumberError ?? undefined}
                fullWidth
              />
            </div>
            <div>
              <label className={styles.fieldLabel}>On Call Doctor</label>
              <Input
                value={draft.doctorLastName}
                onChange={(e) => set('doctorLastName', e.target.value)}
                placeholder="e.g. Dr. Smith"
                fullWidth
              />
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ── Estimated value per lead ──────────────────────────────────────────────────

export function ValueSection({ draft, setConfig, open, onToggle }: SectionProps & OpenProps) {
  const table = draft.config.valueTable ?? {};
  const [newService, setNewService] = useState('');

  const setValue = (key: string, value: number) =>
    setConfig('valueTable', { ...table, [key]: value });
  const removeRow = (key: string) => {
    const next = { ...table };
    delete next[key];
    setConfig('valueTable', next);
  };
  const addRow = () => {
    const key = newService.trim();
    if (!key || table[key] !== undefined) return;
    setConfig('valueTable', { ...table, [key]: 0 });
    setNewService('');
  };

  return (
    <SectionCard
      icon={DollarSign}
      title="Estimated value per lead"
      done
      open={open}
      onToggle={onToggle}
    >
      <p className={styles.hintText}>
        Used to estimate the opportunity value of calls on ROI reports. Shown as{' '}
        <em>opportunity</em>, never counted as revenue. Edit to match your fees.
      </p>
      {Object.entries(table).map(([service, value]) => (
        <div key={service} className={styles.valueRow}>
          <span className={styles.valueService}>{service}</span>
          <div className={styles.valueAmount}>
            <span className={styles.valueCurrency}>$</span>
            <Input
              type="number"
              step={1}
              min={0}
              value={String(value)}
              // Whole dollars — the backend rejects decimals.
              onChange={(e) =>
                setValue(service, Math.max(0, Math.round(Number(e.target.value) || 0)))
              }
            />
            <button
              onClick={() => removeRow(service)}
              className={styles.deleteBtn}
              aria-label={`Remove ${service}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}
      <div className={styles.faqItemRow}>
        <Input
          value={newService}
          onChange={(e) => setNewService(e.target.value)}
          placeholder="Add a service…"
          fullWidth
        />
        <Button variant="secondary" size="sm" leftIcon={<Plus size={13} />} onClick={addRow}>
          Add
        </Button>
      </div>
    </SectionCard>
  );
}
